import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, Mail, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (!email.endsWith('@mypatriotmail.org')) {
      setLocalError('Only @mypatriotmail.org emails are allowed');
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/inbox');
    } else {
      setLocalError(result.error);
    }
  };

  const streakStyle = {
    position: 'absolute',
    width: '300%',
    height: '300%',
    top: '-100%',
    left: '-100%',
    background: 'conic-gradient(from 0deg at 50% 50%, #16162a 0deg, #6D4AFF 30deg, #8B5CF6 60deg, #ec4899 90deg, #f97316 120deg, #6D4AFF 150deg, #16162a 180deg, #16162a 360deg)',
    animation: 'streak-spin 2s linear infinite',
  };

  return (
    <div className="min-h-screen bg-[#16162a] flex items-center justify-center p-4">
      <style>
        {`@keyframes streak-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
      </style>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img 
            src="/mpm-logo.png" 
            alt="MyPatriotMail" 
            className="w-20 h-20 object-contain mb-4 drop-shadow-[0_0_15px_rgba(109,74,255,0.5)]"
          />
          <h1 className="text-2xl font-bold text-white">MyPatriotMail</h1>
          <p className="text-gray-400 text-sm mt-1">Encrypted Email Platform</p>
        </div>

        <div className="relative p-[3px] rounded-xl overflow-hidden">
          <div style={streakStyle} />
          <Card className="relative bg-[#1a1a2e] border-0 rounded-[9px]">
            <CardHeader className="space-y-1">
              <CardTitle className="text-xl text-white">Sign in</CardTitle>
              <CardDescription className="text-gray-400">
                Enter your @mypatriotmail.org credentials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {(localError || error) && (
                  <Alert variant="destructive" className="bg-red-500/10 border-red-500/50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{localError || error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@mypatriotmail.org"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase())}
                      className="pl-10 bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500 focus-visible:ring-[#6D4AFF]"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-gray-300">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-[#6D4AFF] hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500 focus-visible:ring-[#6D4AFF]"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <div className="text-sm text-gray-400 text-center">
                Do not have an account?{' '}
                <Link to="/register" className="text-[#6D4AFF] hover:underline">
                  Create account
                </Link>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Lock className="w-3 h-3" />
                <span>End-to-end encrypted - Internal network only</span>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
